import React from 'react';
import { Box, Switch, Typography, SxProps, Theme } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { InfoTooltip } from 'components/Widgets/InfoTooltip';

type LabelPlacement = 'start' | 'end' | 'top' | 'bottom';
type CustomSwitchSize = 'default' | 'small';

interface CustomSwitchColors {
  thumb?: string;
  trackOn?: string;
  trackOff?: string;
  thumbShadow?: string;
  label?: string;
  disabledOpacity?: number;
}

interface CustomSwitchProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  startLabel?: string;
  endLabel?: string;
  startTooltipLabel?: any;
  endTooltipLabel?: any;
  labelPlacement?: LabelPlacement;
  size?: CustomSwitchSize;
  fullWidth?: boolean;
  minHeight?: string | number;
  name?: string;
  id?: string;
  colors?: CustomSwitchColors;
  containerSx?: SxProps<Theme>;
  switchSx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
}

const CustomSwitch: React.FC<CustomSwitchProps> = ({
  checked,
  onChange,
  disabled = false,
  label,
  startLabel,
  endLabel,
  startTooltipLabel,
  endTooltipLabel,
  labelPlacement = 'start',
  size = 'default',
  fullWidth = false,
  minHeight = 40,
  name,
  id,
  colors,
  containerSx,
  switchSx,
  labelSx,
}) => {
  const theme = useTheme();

  const finalStartLabel = startLabel ?? (labelPlacement === 'start' ? label : undefined);
  const finalEndLabel = endLabel ?? (labelPlacement === 'end' ? label : undefined);

  const switchSize =
    size === 'small'
      ? {
          width: 34,
          height: 20,
          padding: '2px',
          thumb: 16,
          translateX: 'translateX(14px)',
        }
      : {
          width: { xs: 36, sm: 40, md: 42 },
          height: { xs: 22, sm: 24, md: 26 },
          padding: { xs: '2px', sm: '3px' },
          thumb: { xs: 16, sm: 18, md: 20 },
          translateX: {
            xs: 'translateX(14px)',
            sm: 'translateX(15px)',
            md: 'translateX(16px)',
          },
        };

  const labelElement = (labelText: string) => (
    <Typography
      sx={{
        fontSize: { xs: '13px', md: '14px' },
        fontWeight: 500,
        color: colors?.label ?? theme.palette.toggle.text,
        userSelect: 'none',
        lineHeight: 1.2,
        ...labelSx,
      }}
    >
      {labelText}
    </Typography>
  );

  const switchElement = (
    <Switch
      checked={checked}
      disabled={disabled}
      name={name}
      id={id}
      onChange={(e) => onChange(e.target.checked)}
      sx={{
        width: switchSize.width,
        height: switchSize.height,
        padding: 0,
        display: 'flex',

        '& .MuiSwitch-switchBase': {
          padding: switchSize.padding,
          transitionDuration: '200ms',

          '&.Mui-checked': {
            transform: switchSize.translateX,
            color: colors?.thumb ?? theme.palette.toggle.thumb,

            '& + .MuiSwitch-track': {
              backgroundColor: colors?.trackOn ?? theme.palette.toggle.trackOn,
              opacity: 1,
              border: 'none',
            },
          },

          '&:hover': {
            backgroundColor: 'transparent',
          },

          '&.Mui-active .MuiSwitch-thumb': {
            transform: 'scale(0.96)',
          },

          '&.Mui-disabled': {
            color: colors?.thumb ?? theme.palette.toggle.thumb,
          },

          '&.Mui-disabled + .MuiSwitch-track': {
            opacity: colors?.disabledOpacity ?? 0.5,
          },
        },

        '& .MuiSwitch-thumb': {
          boxSizing: 'border-box',
          width: switchSize.thumb,
          height: switchSize.thumb,
          backgroundColor: colors?.thumb ?? theme.palette.toggle.thumb,
          boxShadow: colors?.thumbShadow ?? theme.palette.toggle.thumbShadow,
          transition: 'all 200ms ease',
        },

        '& .MuiSwitch-track': {
          borderRadius: 999,
          backgroundColor: colors?.trackOff ?? theme.palette.toggle.trackOff,
          opacity: 1,
          transition: 'background-color 200ms ease',
        },

        ...switchSx,
      }}
    />
  );

  return (
    <Box
      sx={{
        minHeight,
        width: fullWidth ? '100%' : 'auto',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        ...containerSx,
      }}
    >
      <Box
        sx={{
          margin: 0,
          width: fullWidth ? '100%' : 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: fullWidth ? 'space-between' : 'flex-start',
          gap: 1,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {startTooltipLabel && (
          <InfoTooltip label={startTooltipLabel} />
        )}

        {finalStartLabel && labelElement(finalStartLabel)}

        {switchElement}

        {finalEndLabel && labelElement(finalEndLabel)}

        {endTooltipLabel && (
          <InfoTooltip label={endTooltipLabel} />
        )}
      </Box>
    </Box>
  );
};

export default CustomSwitch;
